import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { Navigate, useLocation } from 'react-router-dom';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  checkActionCode,
  applyActionCode,
  updateProfile as firebaseUpdateProfile,
  updateEmail as firebaseUpdateEmail,
} from 'firebase/auth';
import { getUser, createUser, updateUser } from './db';

const AuthContext = createContext({});

// Email verification and recovery handlers
export async function handleVerifyEmail(actionCode) {
  try {
    await applyActionCode(auth, actionCode);
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
}

export async function handleRecoverEmail(actionCode) {
  try {
    const info = await checkActionCode(auth, actionCode);
    await applyActionCode(auth, actionCode);
    return { success: true, data: info };
  } catch (error) {
    return { success: false, error };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);

        // Load user data from Firestore
        try {
          const firestoreData = await getUser(authUser.uid);
          if (!firestoreData) {
            // Create user document if it doesn't exist
            await createUser(authUser.uid, {
              email: authUser.email,
              name: authUser.displayName || '',
              createdAt: new Date().toISOString(),
              hasSeenWalkthrough: false
            });
            const newData = await getUser(authUser.uid);
            setUserData(newData);
          } else {
            setUserData(firestoreData);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email, password) => {
    try {
      console.log('Attempting signup with email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Signup successful:', userCredential);
      return userCredential;
    } catch (error) {
      console.error('Signup error:', error.code, error.message);
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential;
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async ({ name, email }) => {
    try {
      if (!auth.currentUser) throw new Error('No user logged in');

      // Update Firebase Auth profile
      if (name && name !== auth.currentUser.displayName) {
        await firebaseUpdateProfile(auth.currentUser, { displayName: name });
      }

      if (email && email !== auth.currentUser.email) {
        await firebaseUpdateEmail(auth.currentUser, email);
      }

      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const refreshUserData = async () => {
    if (auth.currentUser?.uid) {
      try {
        const firestoreData = await getUser(auth.currentUser.uid);
        setUserData(firestoreData);
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  const value = {
    user,
    userData,
    loading,
    signIn,
    signUp,
    logout,
    resetPassword,
    signInWithGoogle,
    signup,
    updateProfile,
    refreshUserData,
    isAdmin: user?.email === 'fedify8@gmail.com'
  };

  // Add console log to debug auth context
  console.log('Auth context value:', value);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return (
      <Navigate
        to={`/auth/signin?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  return children;
}

// Higher order component for requiring auth
export function requireAuth(Component) {
  return function WithAuth(props) {
    const auth = useAuth();
    const location = useLocation();

    if (!auth.user) {
      return (
        <Navigate 
          to={`/auth/signin?next=${encodeURIComponent(location.pathname)}`}
          replace
        />
      );
    }

    return <Component {...props} />;
  };
}

export const useAuth = () => {
  const auth = React.useContext(AuthContext);
  console.log('Auth context:', auth);
  console.log('User email:', auth?.user?.email);
  console.log('Is admin:', auth?.isAdmin);
  return auth;
};